import { useStackSiderView } from '@/providers/StackSiderViewProvider';
import { useFormDirtyStore } from '@/stores/form-dirty-store';
import { Drawer as AntDrawer, type DrawerProps } from 'antd';
import { type MouseEvent, useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router';
import { useLeavePageAlertState } from '../Form/LeavePageAlert';

export function Drawer({ onClose, open: _open, ...restProps }: DrawerProps) {
  const { isDirty, reset } = useFormDirtyStore();
  const { state: stackSiderViewState, dispatch } = useStackSiderView();
  const { leavePageAlertReference } = useLeavePageAlertState();
  const [open, setOpen] = useState(_open);
  const isSiderOpen = stackSiderViewState.stack.length > 0;

  const navigate = useNavigate();
  const location = useLocation();
  // useBlocker に "blocked" 状態を発生させるためのダミー遷移を一度だけ発火させる。
  // navigateRef.current が true の間は再発火させないことで無限ループを防ぐ。
  //   1) Drawer を開く要求が来る
  //   2) 未保存フォームがあれば同一 URL へ replace で遷移試行
  //   3) LeavePageAlert が表示され、OK/キャンセルで navigateRef をリセット
  const navigateRef = useRef(false);

  useEffect(() => {
    // Sider が開いている状態で、さらに Drawer を開こうとした場合の処理
    if (isSiderOpen && _open) {
      if (isDirty) {
        // 未保存の変更がある場合: useBlocker を blocked にするための遷移を試みる
        if (!navigateRef.current) {
          navigateRef.current = true;
          navigate(`${location.pathname}${location.search}`, { replace: true });
        }
        // ユーザが "OK" を押して遷移許可した後の処理
        leavePageAlertReference.current.afterAllowAction = () => {
          // ユーザーが離脱を許可したため、フォームのダーティ状態をリセットする
          reset();
          dispatch({ type: 'CLEAR' });
          setOpen(true);
          navigateRef.current = false;
        };
        // "キャンセル" 選択時は何もせずフラグだけ戻す
        leavePageAlertReference.current.afterCancelAction = () => {
          navigateRef.current = false;
          // キャンセル時はドロワーを閉じる
          onClose?.({} as MouseEvent);
        };
        return;
      }
      // 未保存データが無い場合は通常どおり Drawer を開く
      dispatch({ type: 'CLEAR' });
      setOpen(true);
    } else {
      setOpen(_open);
    }
  }, [
    isSiderOpen,
    isDirty,
    dispatch,
    _open,
    leavePageAlertReference,
    navigate,
    location,
    reset,
    onClose,
  ]);

  return (
    <AntDrawer
      mask={false}
      maskClosable={false}
      open={open}
      onClose={onClose}
      {...restProps}
    />
  );
}
